import { BlokProps } from "@/helpers/StoryblokHelpers"
import { StoryblokPageTemplates } from "@/store/story-store"
import {
  ArticlePageStoryblok,
  AuthorPageStoryblok,
  ClinicPageStoryblok,
  FactAdvicePageStoryblok,
  FaqStoryblok,
  PageStoryblok,
  SeoAnatomicalStructureStoryblok,
  SeoConsumeActionStoryblok,
  SeoMedicalRiskFactorStoryblok,
  SeoMedicalSymptomStoryblok,
  SeoMedicalTherapyStoryblok,
  SeoSchemaHowToStepStoryblok
} from "@/types/component-types"
import { getPlainText } from "@/utils/richTextToPlain"
import { useMemo } from "react"
import { StoryblokRichtext } from "storyblok-rich-text-react-renderer"

export const useSeoArticleScript = (story: StoryblokPageTemplates, featuredImage?: string) => {
  return useMemo(
    () => ({
      "@context": "http://schema.org",
      "@type": "Article",
      headline: story?.name,
      author: {
        "@type": "Person",
        name: story?.content.author
      },
      name: story?.name,
      description: story?.content.seo_schema_article_description,
      datePublished: story?.first_published_at,
      dateModified: story?.published_at,
      url: process.env.HOME_URL + story?.full_slug,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": process.env.HOME_URL
      },
      ...(featuredImage && { image: featuredImage }),
      articleSection: story?.content.seo_schema_article_section,
      publisher: {
        "@type": "Organization",
        name: "Doktor.se"
      }
    }),
    [
      featuredImage,
      story?.content.author,
      story?.content.seo_schema_article_section,
      story?.first_published_at,
      story?.name,
      story?.published_at,
      story?.full_slug
    ]
  )
}

export const useSeoFaqScript = (
  blok:
    | BlokProps<ArticlePageStoryblok>["blok"]
    | BlokProps<FactAdvicePageStoryblok>["blok"]
    | BlokProps<ClinicPageStoryblok>["blok"]
    | BlokProps<PageStoryblok>["blok"]
) => {
  return useMemo(() => {
    if (blok.seo_schema_faq && (blok.seo_schema_faq as Array<{ answer: string; question: string }>).length > 0) {
      return {
        "@context": "http://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          (blok.seo_schema_faq as Array<{ answer: string; question: string }>).map(
            (item: { answer: string; question: string }) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer
              }
            })
          )
        ]
      }
    }
    if (blok.faq && (blok.faq as FaqStoryblok[]).length > 0) {
      return {
        "@context": "http://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          (blok.faq as FaqStoryblok[]).map((item: FaqStoryblok) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: getPlainText(item.answer as StoryblokRichtext)
            }
          }))
        ]
      }
    }
  }, [blok])
}

export const useSeoMedicalConditionScript = (story: StoryblokPageTemplates) => {
  return useMemo(() => {
    if (story?.content.seo_schema_medical_condition_name) {
      const signOrSymptom = (story.content.sign_or_symptom as SeoMedicalSymptomStoryblok[])?.map(
        (s: SeoMedicalSymptomStoryblok) => ({
          "@type": "MedicalSignOrSymptom",
          name: s.name
        })
      ) ?? {
        "@type": "MedicalSymptom",
        name: story?.content.seo_schema_medical_condition_symptom
      }

      const possibleTreatment = (story.content.possible_treatment as SeoMedicalTherapyStoryblok[])?.map(
        (s: SeoMedicalTherapyStoryblok) => ({
          "@type": "MedicalTherapy",
          name: s.name
        })
      ) ?? {
        "@type": "MedicalTherapy",
        name: story?.content.seo_schema_medical_condition_possible_treatment
      }

      const riskFactor = (story.content.risk_factor as SeoMedicalRiskFactorStoryblok[])?.map(
        (s: SeoMedicalRiskFactorStoryblok) => ({
          "@type": "MedicalRiskFactor",
          name: s.name,
          description: s.description
        })
      ) ?? {
        "@type": "MedicalRiskFactor",
        name: story?.content.seo_schema_medical_condition_cause
      }

      const associatedAnatomy = (story.content.associated_anatomy as SeoAnatomicalStructureStoryblok[])?.map(
        (s: SeoAnatomicalStructureStoryblok) => ({
          "@type": "AnatomicalStructure",
          name: s.name
        })
      ) ?? {
        "@type": "AnatomicalStructure",
        name: story?.content.seo_schema_medical_condition_anatomy
      }

      const potentialAction = (story.content.potential_action as SeoConsumeActionStoryblok[])?.map(
        (s: SeoConsumeActionStoryblok) => ({
          "@type": "ConsumeAction",
          name: s.name,
          description: s.description
        })
      )

      return {
        "@id": process.env.HOME_URL + story?.slug,
        "@context": "http://schema.org",
        "@type": "MedicalCondition",
        name: story?.content.seo_schema_medical_condition_name,
        alternateName: story?.content.seo_schema_medical_condition_alternate_name,
        description: story?.content.seo_schema_medical_condition_description,
        riskFactor,
        possibleTreatment,
        signOrSymptom,
        associatedAnatomy,
        potentialAction
      }
    }
  }, [story])
}

export const useSeoMedicalWebPageScript = (story: StoryblokPageTemplates) => {
  return useMemo(() => {
    if (story?.content.seo_schema_medical_condition_name) {
      const author = (story?.content.author_ref as AuthorPageStoryblok[])?.map((author: AuthorPageStoryblok) => ({
        "@type": "Person",
        name: author.full_name_text,
        jobTitle: author.position_text,
        affiliation: {
          "@type": "Organization",
          name: "Doktor.se"
        }
      })) ?? {
        "@type": "Person",
        name: story?.content.author,
        affiliation: {
          "@type": "Organization",
          name: "Doktor.se"
        }
      }

      const contentInspector = (story?.content.content_inspectors_ref as AuthorPageStoryblok[])?.map(
        (author: AuthorPageStoryblok) => ({
          "@type": "Person",
          name: author.full_name_text,
          jobTitle: author.position_text,
          affiliation: {
            "@type": "Organization",
            name: "Doktor.se"
          }
        })
      ) ?? {
        "@type": "Person",
        name: story?.content.content_inspector,
        affiliation: {
          "@type": "Organization",
          name: "Doktor.se"
        }
      }

      return {
        "@context": "http://schema.org",
        "@type": "MedicalWebPage",
        id: process.env.HOME_URL + story?.slug,
        headline: story?.name,
        description: story?.content.seo_schema_medical_condition_description,
        isAccessibleForFree: true,
        url: process.env.HOME_URL + story?.full_slug,
        about: {
          "@type": "MedicalCondition",
          id: process.env.HOME_URL + story?.slug,
          name: story?.content.seo_schema_medical_condition_name
        },
        medicalAudience: {
          "@type": "MedicalAudience",
          name: "Patients",
          audienceType: "Patients",
          id: process.env.HOME_URL + story?.slug,
          healthCondition: {
            "@type": "MedicalCondition",
            "@id": process.env.HOME_URL + story?.slug
          }
        },
        datePublished: story?.first_published_at,
        dateModified: story?.published_at,
        lastReviewed: story?.published_at,
        reviewedBy: contentInspector,
        author,
        publisher: {
          "@type": "Organization",
          name: "Doktor.se"
        }
      }
    }
  }, [story])
}

export const useSeoHowToScript = (story: StoryblokPageTemplates) => {
  return useMemo(() => {
    if (story?.content.seo_schema_how_to) {
      return {
        "@context": "http://schema.org",
        "@type": "HowTo",
        "@id": process.env.HOME_URL + story?.slug,
        name: story?.name,
        tool: story?.content.seo_schema_how_to_tool,
        image: story?.content.seo_schema_how_to_image,
        step: (story?.content.seo_schema_how_to as SeoSchemaHowToStepStoryblok[]).map(s => ({
          "@type": "HowToStep",
          text: s.step,
          name: s.name
        }))
      }
    }
  }, [story])
}

export const useSeoMedicalOrganizationScript = (blok: BlokProps<ClinicPageStoryblok>["blok"]) => {
  return useMemo(
    () => ({
      "@context": "http://schema.org",
      "@type": "MedicalOrganization",
      name: blok.seo_schema_medical_organization_name,
      address: {
        "@type": "PostalAddress",
        streetAddress: blok.seo_schema_medical_organization_address_street,
        postalCode: blok.seo_schema_medical_organization_address_postal,
        addressCountry: blok.seo_schema_medical_organization_address_country
      },
      contactPoint: {
        "@type": "ContactPoint",
        telephone: blok.seo_schema_medical_organization_address_telephone
      }
    }),
    [blok]
  )
}

export const useSeoOpeningHoursScript = (blok: BlokProps<ClinicPageStoryblok>["blok"]) => {
  return useMemo(
    () => ({
      "@context": "http://schema.org",
      "@type": "OpeningHoursSpecification",
      dayOfWeek: blok.seo_schema_opening_hours_days as Array<string>,
      opens: blok.seo_schema_opening_hours_opens,
      closes: blok.seo_schema_opening_hours_closes
    }),
    [blok]
  )
}

export const useSeoMedicalTherapyScript = (blok: BlokProps<PageStoryblok>["blok"]) => {
  return useMemo(() => {
    if (blok.seo_schema_medical_therapy_name) {
      return {
        "@context": "http://schema.org",
        "@type": "MedicalTherapy",
        name: blok.seo_schema_medical_therapy_name,
        description: blok.seo_schema_medical_therapy_description,
        therapyType: {
          "@type": "MedicalSpecialty",
          name: blok.seo_schema_medical_therapy_type
        },
        contraindication: {
          "@type": "MedicalContraindication",
          name: blok.seo_schema_medical_therapy_contraindication
        },
        seriousAdverseOutcome: {
          "@type": "MedicalEntity",
          name: blok.seo_schema_medical_therapy_serious_adverse_outcome
        },
        procedureType: blok.seo_schema_medical_therapy_procedure_type
      }
    }
  }, [blok])
}

export const useSeoProductScript = (blok: BlokProps<PageStoryblok>["blok"]) => {
  return useMemo(() => {
    if (blok.seo_schema_product_name) {
      return {
        "@context": "http://schema.org",
        "@type": "Product",
        name: blok.seo_schema_product_name,
        description: blok.seo_schema_product_description,
        offers: {
          "@type": "Offer",
          priceCurrency: blok.seo_schema_product_price_currency,
          price: blok.seo_schema_product_price,
          availability: "http://schema.org/InStock"
        }
      }
    }
  }, [blok])
}
